import { useCallback } from 'react';
import { useAnalytics } from '@context/analytics/analytics.context';
import {
  aiListingAddPhotosEventPayload,
  aiListingDescriptionEventPayload,
  aiListingFieldEditEventPayload,
  aiListingFormSubmitEventPayload,
  aiListingLeaveConfirmationEventPayload,
  aiListingPhotoReorderEventPayload,
  aiListingScanEventPayload,
  aiListingStartEventPayload,
} from '@tracking/ai-listing/ai-listing-tracking';
import {
  AiListingDescriptionEvent,
  AiListingFieldEditEvent,
  AiListingFormSubmitEvent,
  AiListingLeaveConfirmationEvent,
  AiListingStartEvent,
} from '@interfaces/models/internal/analytics-event/ai-listing-event';

/**
 * Tracking hook for the AI listing flow.
 *
 * Depends only on `useAnalytics()` (never `useAiListing()`), so it can be used
 * from components rendered outside the `AiListingProvider` — e.g. the
 * `ListingOptionsButton` in the header / sell page. Per-call dynamic values are
 * passed as arguments.
 */
export const useAiListingTracking = () => {
  const { sendEvent } = useAnalytics();

  const trackListingStart = useCallback(
    (action: AiListingStartEvent['action']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingStartEventPayload, action },
      });
    },
    [sendEvent],
  );

  const trackPhotoScan = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingScanEventPayload });
  }, [sendEvent]);

  const trackAddPhotos = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingAddPhotosEventPayload });
  }, [sendEvent]);

  const trackPhotoReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingPhotoReorderEventPayload, label: fromIndex, value: toIndex },
      });
    },
    [sendEvent],
  );

  const trackLeaveConfirmation = useCallback(
    (action: AiListingLeaveConfirmationEvent['action'], label: AiListingLeaveConfirmationEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingLeaveConfirmationEventPayload, action, label },
      });
    },
    [sendEvent],
  );

  const trackFieldEdit = useCallback(
    (action: AiListingFieldEditEvent['action'], label: AiListingFieldEditEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingFieldEditEventPayload, action, label },
      });
    },
    [sendEvent],
  );

  const trackDescription = useCallback(
    (label: AiListingDescriptionEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingDescriptionEventPayload, label },
      });
    },
    [sendEvent],
  );

  const trackFormSubmit = useCallback(
    (action: AiListingFormSubmitEvent['action']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingFormSubmitEventPayload, action },
      });
    },
    [sendEvent],
  );

  return {
    trackListingStart,
    trackPhotoScan,
    trackAddPhotos,
    trackPhotoReorder,
    trackLeaveConfirmation,
    trackFieldEdit,
    trackDescription,
    trackFormSubmit,
  };
};
